import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { BulkCompleteResult } from '@vagusx/clients';
import { useBulkCompleteTasks } from '@/hooks/use-tasks';
import { useTaskSelection, type TaskSelectionApi } from './use-task-selection';
import { classifyBulkResult, decideSelectAllAction, hasStaleSelection } from './bulk-task-actions';

/**
 * Owns all the state + handlers for the task-inbox bulk-action flow.
 *
 * Extracted from `TaskListPanel` to keep the panel focused on
 * rendering. When future bulk actions land (`bulk-archive`,
 * `bulk-reassign`, etc.), each adds a new mutation hook + handler
 * here, not new lines in the panel. The action bar component then
 * just consumes the API surface returned below.
 *
 * Side effects this hook owns:
 *   - Auto-clear selection when the visible task list changes due to
 *     filter / page change (stale-selection guard).
 *   - Esc-to-clear keyboard shortcut.
 *   - Toast classification (all-success / partial / all-failure)
 *     using pure helpers in `./bulk-task-actions`.
 */

export interface TaskBulkActionsApi {
  /** Selection primitives — passed through to row checkboxes. */
  selection: TaskSelectionApi;
  /** Convenience derived values. */
  selectionMode: boolean;
  allOnPageSelected: boolean;
  /** How many ids are on the current page — drives empty-state rendering of the toolbar tri-state. */
  idsOnPageCount: number;
  /** Whether the bulk-complete mutation is in flight (for button disable). */
  isPending: boolean;
  /** UI handlers for the action bar / row clicks. */
  onCheckboxClick: (id: string, indexOnPage: number, event: React.MouseEvent) => void;
  onToggleSelectAllOnPage: () => void;
  onMarkDone: () => void;
  /** Rows to render in the failure dialog (empty = closed). */
  failureRows: BulkCompleteResult['failed'];
  closeFailureDialog: () => void;
}

export function useTaskBulkActions(idsOnPage: string[]): TaskBulkActionsApi {
  const selection = useTaskSelection();
  const bulkComplete = useBulkCompleteTasks();
  const [failureRows, setFailureRows] = useState<BulkCompleteResult['failed']>([]);

  // Auto-clear selection when the visible task list changes (filter
  // change, pagination, refetch that drops a row, etc.). Catches the
  // "phantom selection" bug where the bar would show "5 selected"
  // pointing at ids that aren't rendered anymore. `idsOnPage` is the
  // primary dep; selection.clear is included because eslint flags it
  // — it's a stable useCallback ref so this is a no-op at runtime.
  const visibleIds = useMemo(() => new Set(idsOnPage), [idsOnPage]);
  useEffect(() => {
    if (selection.count === 0) return;
    if (hasStaleSelection(selection.selected, visibleIds)) {
      selection.clear();
    }
  }, [visibleIds, selection]);

  // Esc clears selection. Quick keyboard exit out of bulk mode.
  useEffect(() => {
    if (selection.count === 0) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') selection.clear();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selection]);

  const selectionMode = selection.count > 0;
  const allOnPageSelected =
    idsOnPage.length > 0 && idsOnPage.every((id) => selection.isSelected(id));

  function onCheckboxClick(id: string, indexOnPage: number, event: React.MouseEvent): void {
    if (event.shiftKey) selection.toggleRange(id, indexOnPage, idsOnPage);
    else selection.toggle(id, indexOnPage);
  }

  function onToggleSelectAllOnPage(): void {
    const action = decideSelectAllAction(allOnPageSelected);
    if (action === 'clear') selection.clear();
    else selection.selectAll(idsOnPage);
  }

  function onMarkDone(): void {
    const ids = Array.from(selection.selected);
    if (ids.length === 0) return;
    bulkComplete.mutate(ids, {
      onSuccess: (result) => {
        const outcome = classifyBulkResult(result);
        if (outcome.kind === 'all-success') {
          toast.success(`${outcome.count} task${outcome.count === 1 ? '' : 's'} marked done`);
        } else if (outcome.kind === 'all-failure') {
          toast.error(`Couldn't mark any tasks done`, {
            action: { label: 'Show details', onClick: () => setFailureRows(outcome.failed) },
          });
        } else {
          toast.success(
            `${outcome.completedCount} task${outcome.completedCount === 1 ? '' : 's'} marked done`
          );
          toast.warning(`${outcome.failedCount} couldn't be updated`, {
            action: { label: 'Show', onClick: () => setFailureRows(outcome.failed) },
          });
        }
        // Always clear — succeeded rows are gone (filter no longer
        // matches), failed rows can be re-selected manually.
        selection.clear();
      },
      // Network / 5xx is handled by the global mutation-error toast.
    });
  }

  return {
    selection,
    selectionMode,
    allOnPageSelected,
    idsOnPageCount: idsOnPage.length,
    isPending: bulkComplete.isPending,
    onCheckboxClick,
    onToggleSelectAllOnPage,
    onMarkDone,
    failureRows,
    closeFailureDialog: () => setFailureRows([]),
  };
}
